import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import classNames from 'classnames';
import type { ComponentProps, CSSProperties, ReactNode } from 'react';

import { Sport } from '@sorare/core/src/__generated__/globalTypes';
import { BrandXXS } from '@sorare/core/src/atoms/typography';
import { CardFooter } from '@sorare/core/src/components/card/CardFooter';
import { gameplayTierL10TabPlacement } from '@sorare/core/src/components/card/GameplayTierBadge';
import { GameplayTierStarFlightSource } from '@sorare/core/src/components/card/GameplayTierStarFlight';
import { NBAGameplayTierBadge } from '@sorare/core/src/components/card/NBAGameplayTierBadge';
import { useFeatureFlags } from '@sorare/core/src/hooks/useFeatureFlags';
import { CARD_HOVER_ROOT } from '@sorare/core/src/style/cardHoverRoot';
import { css } from '@sorare/css';

import { AnyPlayerStatsIcon } from '#src/components/player/AnyPlayerStatsIcon/index.tsx';

import type { NBACardFooter_anyPlayerInterface } from './__generated__/index.graphql.ts';

const badgeStack = css`
  position: relative;
  z-index: 1;
  display: flex;
  justify-content: center;
  margin: calc(-1 * var(--intermediate-unit)) 0 calc(-1 * var(--half-unit));
`;

const badgeLayer = css`
  position: relative;
  z-index: 1;
`;

const BADGE_HEIGHT = 40;

// Zero-size anchor on the hexagon centre, rotated so the score tab pops out
// of the side of the badge the L10 progress dot is on.
const scoreTabAnchor = css`
  position: absolute;
  top: ${BADGE_HEIGHT / 2}px;
  left: 50%;
  transform: rotate(var(--l10-tab-angle));
`;

const scoreTab = css`
  position: absolute;
  bottom: var(--l10-tab-distance);
  left: 0;
  transform: translateX(-50%);
  display: flex;
  justify-content: center;
  min-width: var(--triple-unit);
  padding: 0 var(--half-unit) var(--half-unit);
  background-color: var(--c-nd-50-opaque);
  border-radius: 8px 8px 0 0;

  @media (hover: hover) {
    ${CARD_HOVER_ROOT} & {
      opacity: 0;
      transform: translate(-50%, 100%);
    }

    ${CARD_HOVER_ROOT}:hover &,
    ${CARD_HOVER_ROOT}:has(:focus-visible) & {
      opacity: 1;
      transform: translateX(-50%);
    }
  }

  @media (hover: hover) and (prefers-reduced-motion: no-preference) {
    transition:
      transform 250ms var(--ease-out-quart),
      opacity 250ms var(--ease-out-quart);
  }

  @media (hover: hover) and (prefers-reduced-motion: reduce) {
    ${CARD_HOVER_ROOT} & {
      transform: translateX(-50%);
    }
  }
`;

// The score sets its colour inline, so only !important can override it.
const scoreTabOverStarCap = css`
  & .text-gradient-color {
    --text-gradient-color: var(--c-red-400) !important;
  }
`;

const progressDot = css`
  transform-box: fill-box;
  transform-origin: center;

  @media (hover: hover) {
    ${CARD_HOVER_ROOT}:hover &,
    ${CARD_HOVER_ROOT}:has(:focus-visible) & {
      transform: scale(1.4);
    }
  }

  @media (hover: hover) and (prefers-reduced-motion: no-preference) {
    transition: transform 250ms var(--ease-out-quart);
  }
`;

const scoreTabAnchorStyle = (anyPlayer: NBACardFooter_anyPlayerInterface) => {
  const { gameplayTier, lastTenPlayedSo5AverageScore } = anyPlayer;
  const placement = gameplayTier
    ? gameplayTierL10TabPlacement({
        gameplayTier,
        l10Score: lastTenPlayedSo5AverageScore,
        height: BADGE_HEIGHT,
      })
    : undefined;
  return {
    '--l10-tab-angle': `${placement?.angle ?? 0}deg`,
    '--l10-tab-distance': `${placement?.distance ?? BADGE_HEIGHT / 2}px`,
  } as CSSProperties;
};

export const useNBACardFooterEnabled = (sport: Nullable<Sport>) => {
  const {
    flags: { useGamePlayTiers = false },
  } = useFeatureFlags();

  return useGamePlayTiers && sport === Sport.NBA;
};

type Props = {
  anyPlayer: NBACardFooter_anyPlayerInterface;
  score: ReactNode;
  bonus?: ReactNode;
  onClickStats?: ComponentProps<typeof AnyPlayerStatsIcon>['onClick'];
  flyingStars?: number;
  /** Picking this player would bust the lineup's star cap. */
  overStarCap?: boolean;
} & Pick<
  ComponentProps<typeof CardFooter>,
  'matchups' | 'gameOdds' | 'gameDate' | 'extra' | 'onClick'
>;

export const NBACardFooter = ({
  anyPlayer,
  score,
  bonus,
  onClickStats,
  flyingStars,
  overStarCap,
  ...footerProps
}: Props) => {
  const { gameplayTier } = anyPlayer;
  const badge = (
    <NBAGameplayTierBadge
      anyPlayer={anyPlayer}
      height={BADGE_HEIGHT}
      className={badgeLayer}
      progressDotClassName={progressDot}
      overCap={overStarCap}
    />
  );

  return (
    <CardFooter
      centerChildren
      left={
        <AnyPlayerStatsIcon
          backgroundColor="transparent"
          player={anyPlayer}
          onClick={onClickStats}
        />
      }
      right={bonus}
      {...footerProps}
    >
      {gameplayTier ? (
        <div className={badgeStack}>
          <div
            className={scoreTabAnchor}
            style={scoreTabAnchorStyle(anyPlayer)}
          >
            <BrandXXS
              as="div"
              className={classNames(scoreTab, {
                [scoreTabOverStarCap]: overStarCap,
              })}
            >
              {score}
            </BrandXXS>
          </div>
          {flyingStars ? (
            <GameplayTierStarFlightSource stars={flyingStars}>
              {badge}
            </GameplayTierStarFlightSource>
          ) : (
            badge
          )}
        </div>
      ) : (
        score
      )}
    </CardFooter>
  );
};

NBACardFooter.fragments = {
  anyPlayerInterface: gql`
    fragment NBACardFooter_anyPlayerInterface on AnyPlayerInterface {
      slug
      gameplayTier
      ...AnyPlayerStatsIcon_anyPlayer
      ...NBAGameplayTierBadge_anyPlayerInterface
    }
    ${AnyPlayerStatsIcon.fragments.anyPlayer}
    ${NBAGameplayTierBadge.fragments.anyPlayerInterface}
  ` as TypedDocumentNode<NBACardFooter_anyPlayerInterface>,
};
